// Shade-coverage scorer — answers "what % of the patio is in shade right now?"
//
// Approach: sample a 12×12 grid in PATIO-LOCAL coordinates, transform each
// sample to world space via patioMesh.localToWorld at raycast time (so the
// scorer respects the worldGroup's current rotation), then cast a ray from
// each world-space sample toward the sun. A hit on the fabric means that
// sample is shaded. Pure ray-mesh intersection via THREE.Raycaster — fast
// for ≤200 samples.

import * as THREE from 'three';

const GRID_X = 12;
const GRID_Z = 12;

// Sample area on the patio (in patio-local meters). Slightly inset from the
// edges to avoid sampling exactly at the wall or front rim.
const SAMPLE_W = 4.0;
const SAMPLE_D = 4.0;
const LIFT     = 0.05;     // 5 cm above the plane (patio-local +Z)

export class ShadeScorer {
  constructor() {
    this.raycaster = new THREE.Raycaster();
    this.raycaster.firstHitOnly = true;
    this._localSamples = this._buildSamples();
    this._worldSample  = new THREE.Vector3();      // scratch reused per ray
    this._dir          = new THREE.Vector3();
  }

  _buildSamples() {
    // PlaneGeometry's local frame is XY (with +Z as the plane normal).
    // After the patio mesh's rotation.x = -π/2, local +Z still points along
    // the plane normal (now world up), so LIFT in local Z lifts the sample
    // above the surface in the rotated patio's frame.
    const out = [];
    for (let i = 0; i < GRID_X; i++) {
      for (let j = 0; j < GRID_Z; j++) {
        const x = -SAMPLE_W / 2 + (SAMPLE_W / (GRID_X - 1)) * i;
        const y = -SAMPLE_D / 2 + (SAMPLE_D / (GRID_Z - 1)) * j;
        out.push(new THREE.Vector3(x, y, LIFT));
      }
    }
    return out;
  }

  // sunWorld:   THREE.Vector3        — directional light position (world space).
  // casters:    THREE.Mesh[]         — meshes to test for ray hits (fabric +
  //                                    drop screen when deployed).
  // patioMesh:  THREE.Mesh           — patio plane; its world matrix tells us
  //                                    where the (rotating) patio currently sits.
  // Returns 0..1 fraction shaded.
  score(sunWorld, casters, patioMesh) {
    if (!casters || casters.length === 0 || !sunWorld || !patioMesh) return 0;
    if (sunWorld.y <= 0.01) return 0;        // sun below horizon → nothing to shade

    patioMesh.updateWorldMatrix(true, false);

    let hits = 0;
    for (const localPt of this._localSamples) {
      this._worldSample.copy(localPt);
      patioMesh.localToWorld(this._worldSample);     // transform per current rotation
      this._dir.copy(sunWorld).sub(this._worldSample).normalize();
      this.raycaster.set(this._worldSample, this._dir);
      const hit = this.raycaster.intersectObjects(casters, false);
      if (hit.length > 0) hits++;
    }
    return hits / this._localSamples.length;
  }

  patioSize() { return { width: SAMPLE_W, depth: SAMPLE_D }; }
}
